import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUp, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DocumentPreview from "@/components/application/DocumentPreview";

interface CPCSectionProps {
  cpcFrontFile: File | null;
  setCpcFrontFile: (file: File | null) => void;
  cpcFrontPreview: string | null;
  setCpcFrontPreview: (preview: string | null) => void;
  cpcBackFile: File | null;
  setCpcBackFile: (file: File | null) => void;
  cpcBackPreview: string | null;
  setCpcBackPreview: (preview: string | null) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

const CPCSection: React.FC<CPCSectionProps> = ({
  cpcFrontFile,
  setCpcFrontFile,
  cpcFrontPreview,
  setCpcFrontPreview,
  cpcBackFile,
  setCpcBackFile,
  cpcBackPreview,
  setCpcBackPreview,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast();

  const handleFrontFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      setCpcFrontFile(f);
      const fileUrl = URL.createObjectURL(f);
      setCpcFrontPreview(fileUrl);
    }
  };

  const handleBackFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      setCpcBackFile(f);
      const fileUrl = URL.createObjectURL(f);
      setCpcBackPreview(fileUrl);
    }
  };

  const triggerFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hasFront = Boolean(cpcFrontFile) || Boolean(cpcFrontPreview);
    const hasBack = Boolean(cpcBackFile) || Boolean(cpcBackPreview);
    if (!hasFront || !hasBack) {
      toast({
        title: "Missing File",
        description: "Please upload both front and back of your CPC card.",
        variant: "destructive",
      });
      return;
    }
    onSubmit();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>CPC Upload</CardTitle>
        <CardDescription>
          Upload clear images or scans of both sides of your CPC card.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cpcFrontFile">CPC Front</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => triggerFileInput("cpcFrontFile")}
              >
                {cpcFrontPreview ? (
                  <div className="flex flex-col items-center space-y-2">
                    <DocumentPreview
                      src={cpcFrontPreview}
                      alt="CPC front preview"
                      file={cpcFrontFile}
                      imageClassName="max-h-32 object-contain"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCpcFrontFile(null);
                        setCpcFrontPreview(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <FileUp className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Click to upload CPC front
                    </p>
                  </>
                )}
              </div>
              <Input
                id="cpcFrontFile"
                type="file"
                className="hidden"
                onChange={handleFrontFileChange}
                accept="image/png,image/jpeg,application/pdf"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cpcBackFile">CPC Back</Label>
              <div
                className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => triggerFileInput("cpcBackFile")}
              >
                {cpcBackPreview ? (
                  <div className="flex flex-col items-center space-y-2">
                    <DocumentPreview
                      src={cpcBackPreview}
                      alt="CPC back preview"
                      file={cpcBackFile}
                      imageClassName="max-h-32 object-contain"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCpcBackFile(null);
                        setCpcBackPreview(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <FileUp className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Click to upload CPC back
                    </p>
                  </>
                )}
              </div>
              <Input
                id="cpcBackFile"
                type="file"
                className="hidden"
                onChange={handleBackFileChange}
                accept="image/png,image/jpeg,application/pdf"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb hover:bg-amber-500 text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit CPC"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default CPCSection;
